import React, { useContext, useEffect, useState } from 'react';
import { PlusIcon, EllipsisVerticalIcon, EyeIcon, PencilIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/Dropdown';
import { Badge } from '../../../components/Badge';
import { Link } from '../../../components/Link';
import { Button } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import { Divider } from '../../../components/Divider';
import { Avatar } from '../../../components/Avatar';
import Loading from '../../../components/Loading';
import Pagination from '../../../components/Pagination'; // Import the Pagination component
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../../../components/Dialog';
import { useStockOut } from '../../../hooks/useStockOut';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import dateFormat from 'dateformat';
import { UserContext } from '../../../context/userContext';
import { StockOut } from '../../../models/StockOut';
import { useWarehouses } from '../../../hooks/useWarehouse';
import { Text } from '../../../components/Text';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';

const COLUMNS = [
    { key: 'customer', label: 'Customer' },
    { key: 'invoice', label: 'Invoice No' },
    { key: 'logistic', label: 'Logistic' },
    { key: 'issueDate', label: 'Issue Date' },
    { key: 'status', label: 'Status' },
    { key: 'created', label: 'Created on' },
];
const SORT_OPTIONS = [
    { label: 'Newest first', value: 'newest' },
    { label: 'Oldest first', value: 'oldest' },
];

const StockOutPage: React.FC = () => {
    const { currentUser } = useContext(UserContext); // Get current user info
    const { loading, error, deleteStockOut, fetchStockOuts, stockOuts } = useStockOut();
    const [searchQuery, setSearchQuery] = useState<string>(''); // State to store the search query
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isOpen, setIsOpen] = useState(false); // Dialog open state
    const [isDeleting, setIsDeleting] = useState(false); // Loading state for deletion
    const [selectedStockOutId, setSelectedStockOutId] = useState<string | null>(null); // ID of the stockOut to be deleted
    const navigate = useNavigate();
    const { warehouses } = useWarehouses();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState('newest');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterWarehouseId, setFilterWarehouseId] = useState('');

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterWarehouseId('');
    };

    const availableYears = Array.from(
        new Set(
            (Array.isArray(stockOuts) ? stockOuts : [])
                .map((s) => new Date(s.created_time).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear, filterWarehouseId].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
        ...(filterWarehouseId ? [{
            key: 'warehouse',
            label: `Warehouse: ${warehouses?.find((w) => w.id?.toString() === filterWarehouseId)?.warehouse_name || filterWarehouseId}`,
            onClear: () => setFilterWarehouseId(''),
        }] : []),
    ];

    useEffect(() => {
        document.title = "Stock Outs";
    }, []);

    // Filter StockOut based on the search query
    const filteredStockOut = Array.isArray(stockOuts)
        ? stockOuts.filter((stockOut) => {
            const matchesSearch = stockOut.invoice_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                stockOut.reference_no?.toLowerCase().includes(searchQuery.toLowerCase())
            const matchesStartDate = !filterStartDate || new Date(stockOut.created_time) >= new Date(filterStartDate);
            const matchesEndDate = !filterEndDate || new Date(stockOut.created_time) <= new Date(filterEndDate);
            const matchesYear = !filterYear || new Date(stockOut.created_time).getFullYear().toString() === filterYear;
            const matchesWarehouse = !filterWarehouseId || stockOut.warehouse_id === Number(filterWarehouseId);

            return matchesSearch && matchesStartDate && matchesEndDate && matchesYear && matchesWarehouse;
        }) : [];

    const handleOpenDeleteDialog = (StockOutId: string) => {
        setSelectedStockOutId(StockOutId);
        setIsOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedStockOutId) return;

        setIsDeleting(true);
        try {
            await deleteStockOut(Number(selectedStockOutId)); // Call the delete API
            fetchStockOuts();
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to delete Stock Out data:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Stock Out</Heading>
                </div>
                <Button onClick={() => navigate('/gp/stocks/stockout/add')}><PlusIcon />Create new Stock-out</Button>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search stock out…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />

            <ul>
                {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                {error && <p className='py-16 text-center text-red-700'>{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    <TableHeader>Reference No</TableHeader>
                                    {isColumnVisible(columnVisibility, 'customer') && <TableHeader>Customer</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'invoice') && <TableHeader>Invoice No</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'logistic') && <TableHeader>Logistic</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'issueDate') && <TableHeader>Issue Date</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'status') && <TableHeader>Status</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'created') && <TableHeader>Created on</TableHeader>}
                                    <TableHeader className="text-center">Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(filteredStockOut.length > 0) ? [...filteredStockOut].sort((a, b) => {
                                    const diff = new Date(a.created_time).getTime() - new Date(b.created_time).getTime();
                                    return sortOrder === 'newest' ? -diff : diff;
                                }).map((stockOut, index) => (
                                    <TableRow key={stockOut.id} title={`${stockOut.reference_no}`}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell >{stockOut.reference_no}</TableCell>
                                        {isColumnVisible(columnVisibility, 'customer') && <TableCell>{stockOut.customer_fullname}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'invoice') && <TableCell className="text-zinc-500">{stockOut.invoice_no}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'logistic') && <TableCell className="text-zinc-500">{stockOut.logistic_fullname}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'issueDate') && <TableCell className="text-zinc-500">{dateFormat(stockOut.issue_date?.toString(), "mmm dd, yyyy")}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'status') && <TableCell className="text-zinc-500">{stockOut.status}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'created') && <TableCell className="text-zinc-500">{dateFormat(stockOut.created_time?.toString(), "mmm dd, yyyy")}</TableCell>}
                                        <TableCell className="py-5 grid grid-cols-3 text-zinc-500">
                                            <a onClick={() => navigate(`/gp/stocks/stockout/edit/${stockOut.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <PencilIcon className='h-5 w-5' />
                                            </a>
                                            <a onClick={() => navigate(`/gp/stocks/stockout/view/${stockOut.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <EyeIcon className='h-5 w-5' />
                                            </a>

                                            <Dropdown>
                                                <DropdownButton plain aria-label="More options" className='h-5 w-5' >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom end">
                                                    <DropdownItem onClick={() => handleOpenDeleteDialog(stockOut.id.toString())}>Delete</DropdownItem>
                                                    <DropdownItem onClick={() => navigate(`/gp/stocks/stockout/add/items/${stockOut.id}`)}>Add Stock-out Items</DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                )) : <TableRow>
                                    <TableCell colSpan={9} className="text-center py-8">No stock out found.</TableCell>
                                </TableRow>}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                {(currentUser?.role.toString() === "1") && (
                    <div>
                        <Text>By Warehouse</Text>
                        <Select value={filterWarehouseId} onChange={(e) => setFilterWarehouseId(e.target.value)}>
                            <option value="">All Warehouses</option>
                            {warehouses?.map((warehouse) => (
                                <option key={warehouse.id} value={warehouse.id?.toString()}>
                                    {warehouse.warehouse_name}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Delete Stock Out</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this Stock Out data? This action cannot be undone.
                </DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loading /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default StockOutPage;
